// src/pages/OrderDetails.jsx

import React, { useEffect, useState } from "react";
import styles from "../../styles/styles";
import { BsFillBagFill } from "react-icons/bs";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";

// Helper function to format variant details
const formatVariant = (variant) => {
  if (!variant || Object.keys(variant).length === 0) return "";
  return Object.entries(variant)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
};

// Compare variant objects
const variantEquals = (v1, v2) => {
  if (!v1 && !v2) return true;
  if (!v1 || !v2) return false;
  return JSON.stringify(v1) === JSON.stringify(v2);
};

const getQuantitySuppliedForItem = (deliveryPackages, cartItem, excludePackageId = null) => {
  let totalSupplied = 0;
  deliveryPackages.forEach((pkg) => {
    if (excludePackageId && pkg._id === excludePackageId) {
      // exclude that package
      return;
    }
    (pkg.items || []).forEach((pItem) => {
      if (
        pItem.productId === cartItem.productId._id &&
        variantEquals(pItem.variant, cartItem.variant)
      ) {
        totalSupplied += pItem.quantitySupplied;
      }
    });
  });
  return totalSupplied;
};

const OrderDetails = () => {
  const { orders, isLoading } = useSelector((state) => state.order);
  const { seller } = useSelector((state) => state.seller);
  const dispatch = useDispatch();
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const { id } = useParams();

  // State for Delivery Packages
  const [deliveryPackages, setDeliveryPackages] = useState([]);

  // For "Add Package" Modal
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packageItems, setPackageItems] = useState([]);

  // For "Edit Package" Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPackageId, setEditPackageId] = useState("");
  const [editPackageItems, setEditPackageItems] = useState([]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (id) {
        try {
          const response = await axios.get(`${server}/order/get-shop-order/${id}`, { withCredentials: true });
          setOrder(response.data.order);
          setDeliveryPackages(response.data.order.deliveryPackages || []);
        } catch (error) {
          toast.error("Failed to fetch order details");
        }
      }
    };
    fetchOrderDetails();
  }, [id]);


  const refundOrderUpdateHandler = async () => {
    try {
      await axios.put(
        `${server}/order/order-refund-success/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success("Order updated!");
      dispatch(getAllOrdersOfShop(seller._id));
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };


  // Functions to handle Delivery Packages

  // Open Add Package Modal
  const handleOpenAddPackage = () => {
    if (!order || !order.cart) return;
    // For each cart item, figure out how many units are left unsupplied
    const initialItems = order.cart.map((cItem) => {
      const alreadySupplied = getQuantitySuppliedForItem(deliveryPackages, cItem);
      const maxSupply = cItem.qty - alreadySupplied;
      return {
        productId: cItem.productId._id,
        variant: cItem.variant || null,
        name: cItem.productId.name,
        maxSupply,
        quantitySupplied: 0,
      };
    });
    setPackageItems(initialItems);
    setShowPackageModal(true);
  };

  // Close Add Package Modal
  const handleCloseAddPackage = () => {
    setShowPackageModal(false);
    setPackageItems([]);
  };

  // Save New Package
  const handleSaveNewPackage = async () => {
    try {
      // Filter out zero-quantity
      const validItems = packageItems.filter((i) => i.quantitySupplied > 0);
      if (!validItems.length) {
        toast.error("Enter at least one non-zero quantity!");
        return;
      }
      await axios.post(
        `${server}/order/shop/add-delivery-package/${id}`,
        { items: validItems },
        { withCredentials: true }
      );
      toast.success("Delivery package created!");

      // Refetch
      const refetch = await axios.get(`${server}/order/get-shop-order/${id}`, {
        withCredentials: true,
      });
      setOrder(refetch.data.order);
      setDeliveryPackages(refetch.data.order.deliveryPackages);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create package!");
    } finally {
      handleCloseAddPackage();
    }
  };

  // Open Edit Package Modal
  const handleOpenEditPackage = (pkg) => {
    setEditPackageId(pkg._id);

    if (!order || !order.cart) return;
    // Build an array for every cart item in the order
    // and check how many are assigned in other packages (exclude this one)
    const allItems = order.cart.map((cItem) => {
      // How many have been assigned in other packages
      const assignedElsewhere = getQuantitySuppliedForItem(deliveryPackages, cItem, pkg._id);
      const maxSupply = cItem.qty - assignedElsewhere;

      // See if this cart item is in the current package
      const existing = pkg.items.find(
        (pIt) =>
          pIt.productId === cItem.productId._id &&
          variantEquals(pIt.variant, cItem.variant)
      );
      const alreadyInPackageQty = existing ? existing.quantitySupplied : 0;

      return {
        productId: cItem.productId._id,
        variant: cItem.variant || null,
        name: cItem.productId.name,
        maxSupply,
        quantitySupplied: alreadyInPackageQty,
      };
    });

    setEditPackageItems(allItems);
    setShowEditModal(true);
  };

  // Close Edit Package Modal
  const handleCloseEditPackage = () => {
    setShowEditModal(false);
    setEditPackageId("");
    setEditPackageItems([]);
  };

  // Save Edited Package
  const handleSaveEditedPackage = async () => {
    try {
      // Filter out zero quantity
      const validItems = editPackageItems.filter((i) => i.quantitySupplied > 0);
      await axios.put(
        `${server}/order/shop/edit-delivery-package/${id}/${editPackageId}`,
        { items: validItems },
        { withCredentials: true }
      );
      toast.success("Delivery package updated!");

      // Refetch
      const refetch = await axios.get(`${server}/order/get-shop-order/${id}`, {
        withCredentials: true,
      });
      setOrder(refetch.data.order);
      setDeliveryPackages(refetch.data.order.deliveryPackages);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update package!");
    } finally {
      handleCloseEditPackage();
    }
  };

  // Delete a Package
  const handleDeletePackage = async (pkgId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) return;
    try {
      await axios.delete(
        `${server}/order/shop/delete-delivery-package/${id}/${pkgId}`,
        { withCredentials: true }
      );
      toast.success("Package deleted!");
      // Refetch
      const refetch = await axios.get(`${server}/order/get-shop-order/${id}`, {
        withCredentials: true,
      });
      setOrder(refetch.data.order);
      setDeliveryPackages(refetch.data.order.deliveryPackages);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete package!");
    }
  };

  // Update Package Status
  const handlePackageStatusChange = async (pkgId, newStatus) => {
    try {
      const pkg = deliveryPackages.find((p) => p._id === pkgId);
      if (!pkg) return;

      // Disallow revert from certain statuses
      if (
        (pkg.status === "Packed and ready for dispatch" || pkg.status === "Delivered") &&
        newStatus === "Pending"
      ) {
        toast.error("Cannot revert from 'Packed and ready for dispatch' to 'Pending'!");
        return;
      }

      await axios.put(
        `${server}/order/shop/update-delivery-package-status/${id}/${pkgId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success("Package status updated!");

      // Refetch
      const refetch = await axios.get(`${server}/order/get-shop-order/${id}`, {
        withCredentials: true,
      });
      setOrder(refetch.data.order);
      setDeliveryPackages(refetch.data.order.deliveryPackages);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update package status!");
    }
  };

  // Loading State
  if (isLoading || !order) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
      </div>
    );
  }

  // Error State
  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-xl text-red-500">Order not found.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="w-full flex flex-col sm:flex-row items-center justify-between mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <BsFillBagFill size={30} color="#2F855A" />
          <h1 className="pl-2 text-2xl font-semibold">Seller Order Details</h1>
        </div>
        <Link to="/dashboard-orders">
          <div
            className={`${styles.button} bg-pink-100 rounded-md text-pink-600 font-semibold h-12 px-4 text-lg hover:bg-pink-200 transition-colors duration-200`}
          >
            Order List
          </div>
        </Link>
      </div>

      {/* Order Info */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <h5 className="text-gray-600 mb-2 sm:mb-0">
            <span className="font-medium">Order ID:</span> #{order._id}
          </h5>
          <h5 className="text-gray-600 mb-2 sm:mb-0">
            <span className="font-medium">Placed on:</span> {new Date(order.createdAt).toLocaleDateString()}
          </h5>
          <h5 className="text-green-700 font-semibold">
            <span className="font-medium">Total Price:</span> Rs {Number(order.shopTotalOrderPriceWithoutCommission).toFixed(2)}
          </h5>
        </div>
      </div>

      {/* Order Items */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Image</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Make</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST %</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price Without GST</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty (Ordered)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Supplied</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {order.cart.map((cItem, idx) => {
              let totalSupplied = 0;
              deliveryPackages.forEach((pkg) => {
                (pkg.items || []).forEach((pIt) => {
                  if (
                    pIt.productId === cItem.productId._id &&
                    variantEquals(pIt.variant, cItem.variant)
                  ) {
                    totalSupplied += pIt.quantitySupplied;
                  }
                });
              });
              const balance = cItem.qty - totalSupplied;

              return (
                <tr key={idx} className="hover:bg-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap">
                    {cItem.productId?.images?.[0]?.url ? (
                      <img
                        src={cItem.productId.images[0].url}
                        alt={cItem.productId.name}
                        className="w-20 h-20 object-contain rounded"
                      />
                    ) : (
                      <img
                        src="/placeholder-image.png"
                        alt="placeholder"
                        className="w-20 h-20 object-contain rounded"
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {cItem.productId.name}
                    {cItem.variant && Object.keys(cItem.variant).length > 0 && (
                      <span className="text-sm text-gray-500">
                        {" "}
                        ({formatVariant(cItem.variant)})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {cItem.productId.description}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {cItem.productId.tags}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {cItem.productId.gstPercentage} %
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                  Rs { (cItem.totalPriceWithoutCommission / (1 + (cItem.productId.gstPercentage/100)) ).toFixed(2) }
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {cItem.qty} {cItem.productId.pricePerQty}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {totalSupplied}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {balance}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Delivery Package Button */}
      <div className="mb-6">
        <button
          onClick={handleOpenAddPackage}
          className="py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded"
        >
          Add Delivery Package
        </button>
      </div>

      {/* Delivery Packages Table */}
      {deliveryPackages.length > 0 && (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-4">
            Delivery Packages (Split Shipments)
          </h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Package ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveryPackages.map((pkg) => (
                <tr key={pkg._id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">{pkg._id}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {(pkg.items || []).map((it, i2) => (
                      <div key={i2}>
                        <strong>{it.name}</strong>
                        {it.variant && (
                          <span className="text-sm text-gray-500">
                            {" "}
                            ({formatVariant(it.variant)})
                          </span>
                        )}
                        = {it.quantitySupplied}
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {(() => {
                      if (pkg.status === "Pending") {
                        return (
                          <select
                            value="Pending"
                            onChange={(e) =>
                              handlePackageStatusChange(pkg._id, e.target.value)
                            }
                            className="border border-gray-300 rounded-md px-2 py-1"
                          >
                            <option value="Pending">Pending</option>
                            <option value="Packed and ready for dispatch">
                              Packed and ready for dispatch
                            </option>
                            <option value="Delivered">Delivered</option>
                          </select>
                        );
                      } else if (pkg.status === "Packed and ready for dispatch") {
                        return (
                          <select
                            value="Packed and ready for dispatch"
                            onChange={(e) =>
                              handlePackageStatusChange(pkg._id, e.target.value)
                            }
                            className="border border-gray-300 rounded-md px-2 py-1"
                          >
                            <option value="Packed and ready for dispatch">
                              Packed and ready for dispatch
                            </option>
                            <option value="Delivered">Delivered</option>
                          </select>
                        );
                      } else if (pkg.status === "Delivered") {
                        return (
                          <select
                            value="Delivered"
                            onChange={() => {
                              toast.error("Cannot revert from Delivered!");
                            }}
                            className="border border-gray-300 rounded-md px-2 py-1"
                          >
                            <option value="Delivered">Delivered</option>
                          </select>
                        );
                      } else {
                        return <span className="text-gray-700">{pkg.status}</span>;
                      }
                    })()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700 space-x-2">
                    {pkg.status === "Pending" && (
                      <>
                        <button
                          onClick={() => handleOpenEditPackage(pkg)}
                          className="px-2 py-1 bg-yellow-400 text-white rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePackage(pkg._id)}
                          className="px-2 py-1 bg-red-500 text-white rounded"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {(pkg.status === "Packed and ready for dispatch" ||
                      pkg.status === "Delivered") && (
                      null
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Status */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6">
        <h4 className="text-xl font-semibold mb-4">Total Order Status</h4>
        <h4 className="text-2xl font-bold">{order.status}</h4>
      </div>

      {/* Add Delivery Package Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={handleCloseAddPackage}
          />
          <div className="relative bg-white w-11/12 max-w-xl rounded shadow-lg flex flex-col p-6 max-h-[80vh]">
            {/* Modal Header */}
            <h2 className="text-xl font-semibold mb-4">Create a Delivery Package</h2>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full mb-4">
                <thead>
                  <tr>
                    <th className="border p-2">Item</th>
                    <th className="border p-2">Order Balance</th>
                    <th className="border p-2">Supply Now</th>
                  </tr>
                </thead>
                <tbody>
                  {packageItems.map((itm, idx) => (
                    <tr key={idx}>
                      <td className="border p-2">
                        {itm.name}
                        {itm.variant && Object.keys(itm.variant).length > 0 && (
                          <span className="ml-2 text-sm">
                            ({formatVariant(itm.variant)})
                          </span>
                        )}
                      </td>
                      <td className="border p-2 text-center">{itm.maxSupply}</td>
                      <td className="border p-2 text-center">
                        <input
                          type="number"
                          min="0"
                          max={itm.maxSupply}
                          value={itm.quantitySupplied}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setPackageItems((prev) =>
                              prev.map((row, rIdx) =>
                                rIdx === idx
                                  ? { ...row, quantitySupplied: isNaN(val) ? 0 : val }
                                  : row
                              )
                            );
                          }}
                          className="w-16 border rounded p-1 text-center"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={handleCloseAddPackage}
                className="py-2 px-4 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewPackage}
                className="py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded"
              >
                Save Package
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Delivery Package Modal */}
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={handleCloseEditPackage}
          />
          <div className="relative bg-white w-11/12 max-w-xl rounded shadow-lg flex flex-col p-6 max-h-[80vh]">
            {/* Modal Header */}
            <h2 className="text-xl font-semibold mb-4">Edit Delivery Package</h2>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full mb-4">
                <thead>
                  <tr>
                    <th className="border p-2">Item</th>
                    <th className="border p-2">Max Available</th>
                    <th className="border p-2">Supply Now</th>
                  </tr>
                </thead>
                <tbody>
                  {editPackageItems.map((itm, idx) => (
                    <tr key={idx}>
                      <td className="border p-2">
                        {itm.name}
                        {itm.variant && Object.keys(itm.variant).length > 0 && (
                          <span className="text-sm text-gray-500">
                            {" "}
                            ({formatVariant(itm.variant)})
                          </span>
                        )}
                      </td>
                      <td className="border p-2 text-center">{itm.maxSupply}</td>
                      <td className="border p-2 text-center">
                        <input
                          type="number"
                          min="0"
                          max={itm.maxSupply}
                          value={itm.quantitySupplied}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setEditPackageItems((prev) =>
                              prev.map((row, rIdx) =>
                                rIdx === idx
                                  ? { ...row, quantitySupplied: isNaN(val) ? 0 : val }
                                  : row
                              )
                            );
                          }}
                          className="w-16 border rounded p-1 text-center"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={handleCloseEditPackage}
                className="py-2 px-4 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditedPackage}
                className="py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded"
              >
                Update Package
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
